/**
 * Entry-intent expiry.
 *
 * The persisted entry intent (org / squad-invite / guest / cold) used to live forever
 * in localStorage/AsyncStorage — it was never cleared, so an org/squad-invite intent
 * from a link tapped long ago kept re-driving post-tutorial routing ("keeps trying to
 * join a World Cup I joined ages ago") on every visit. We bound its lifetime the same
 * way the sibling pending-WC-squad-join code does (savedAt + TTL), so a stale intent
 * expires and the user is treated as organic again.
 *
 * A fresh invite (within the TTL) still routes to sign-up — the product decision that
 * org/squad members need a real account is unchanged; only stale intents expire.
 */

import type { EntryIntent } from '../types/entryIntent.types';

/** Drop a persisted entry intent after 7 days — mirrors PENDING_WC_SQUAD_JOIN_TTL_MS. */
export const ENTRY_INTENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * True when a rehydrated entry intent should be discarded.
 *
 * - No intent → nothing to expire (false).
 * - Intent present but no `savedAt` → legacy record written before TTL existed; its age
 *   is unknown, so treat it as stale and drop it (this is what unsticks users on the
 *   first load after the fix ships).
 * - Intent present with `savedAt` older than the TTL → stale, drop it.
 */
export function shouldExpireEntryIntent(
  intent: EntryIntent | null,
  savedAt: number | null | undefined,
  now: number,
  ttlMs: number = ENTRY_INTENT_TTL_MS,
): boolean {
  if (!intent) return false;
  if (typeof savedAt !== 'number') return true;
  return now - savedAt > ttlMs;
}
